import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { getPayments, createPayment, updatePayment, deletePayment, getPaymentMethods, getAllBalances, getClients, getClientBalance, setInitialBalance, normalizeAmounts } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Loader2, DollarSign, TrendingUp, TrendingDown, 
  CreditCard, Trash2, Edit2, Search, ArrowUpRight, ArrowDownRight,
  Wallet, Receipt, Users, Eye, Ship, X, Calculator
} from 'lucide-react';

export default function PaymentsPage() {
  const { t } = useI18n();
  const { hasField, user } = useAuth();
  const [activeTab, setActiveTab] = useState('balances');
  const [loading, setLoading] = useState(true);
  const [normalizing, setNormalizing] = useState(false);
  const [payments, setPayments] = useState([]);
  const [balances, setBalances] = useState({ balances: [], totals: {} });
  const [clients, setClients] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showInitialBalanceForm, setShowInitialBalanceForm] = useState(false);
  const [initialBalanceForm, setInitialBalanceForm] = useState({
    amount: '',
    as_of_date: '',
    notes: '',
  });
  const [savingInitialBalance, setSavingInitialBalance] = useState(false);
  
  const [form, setForm] = useState({
    client_id: '',
    amount: '',
    payment_method: 'TRANSFERENCIA',
    payment_date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [paymentsRes, balancesRes, clientsRes, methodsRes] = await Promise.all([
        getPayments(),
        getAllBalances(),
        getClients(),
        getPaymentMethods(),
      ]);
      setPayments(paymentsRes.data);
      setBalances(balancesRes.data);
      setClients(clientsRes.data);
      setPaymentMethods(methodsRes.data);
    } catch (err) {
      toast.error(t('payments.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewClient = async (clientId) => {
    setSelectedClient(clientId);
    setLoadingDetail(true);
    try {
      const res = await getClientBalance(clientId);
      setClientDetail(res.data);
    } catch (err) {
      toast.error(t('payments.clientDetailError'));
      setSelectedClient(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedClient(null);
    setClientDetail(null);
    setShowInitialBalanceForm(false);
  };

  const handleEditInitialBalance = () => {
    setInitialBalanceForm({
      amount: clientDetail?.initial_balance?.toString() || '',
      as_of_date: clientDetail?.initial_balance_date || '',
      notes: t('payments.pendingBalance'),
    });
    setShowInitialBalanceForm(true);
  };

  const handleSaveInitialBalance = async () => {
    if (!initialBalanceForm.amount || !initialBalanceForm.as_of_date) {
      toast.error(t('payments.amountDateRequired'));
      return;
    }
    setSavingInitialBalance(true);
    try {
      await setInitialBalance(
        clientDetail.client_id,
        parseFloat(initialBalanceForm.amount),
        initialBalanceForm.as_of_date,
        initialBalanceForm.notes
      );
      toast.success(t('payments.initialBalanceSaved'));
      setShowInitialBalanceForm(false);
      // Reload client detail
      const res = await getClientBalance(clientDetail.client_id);
      setClientDetail(res.data);
      // Reload balances
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('payments.saveError'));
    } finally {
      setSavingInitialBalance(false);
    }
  };

  const handleNewPayment = () => {
    setEditing(null);
    setForm({
      client_id: '',
      amount: '',
      payment_method: 'TRANSFERENCIA',
      payment_date: new Date().toISOString().split('T')[0],
      reference: '',
      notes: '',
    });
    setShowForm(true);
  };

  const handleNormalize = async () => {
    if (!window.confirm('Esto limpiará los decimales basura de todos los costos y pagos ya registrados de tu organización (los deja con máximo 4 decimales, como Excel; en pantalla se muestran 2). ¿Continuar?')) return;
    setNormalizing(true);
    try {
      const res = await normalizeAmounts();
      toast.success(res.data?.message || 'Montos normalizados');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al normalizar montos');
    } finally {
      setNormalizing(false);
    }
  };

  const handleEditPayment = (payment) => {
    setEditing(payment);
    setForm({
      client_id: payment.client_id,
      amount: payment.amount.toString(),
      payment_method: payment.payment_method,
      payment_date: payment.payment_date,
      reference: payment.reference || '',
      notes: payment.notes || '',
    });
    setShowForm(true);
  };

  const handleDeletePayment = async (payment) => {
    if (!confirm(`¿Eliminar el pago de $${payment.amount.toLocaleString()} de ${payment.client_name}?`)) return;
    
    try {
      await deletePayment(payment.id);
      toast.success(t('payments.paymentDeleted'));
      loadData();
    } catch (err) {
      toast.error(t('payments.deleteError'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.amount || !form.payment_date) {
      toast.error(t('common.required'));
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        amount: parseFloat(form.amount),
      };
      
      if (editing) {
        await updatePayment(editing.id, data);
        toast.success(t('payments.paymentUpdated'));
      } else {
        await createPayment(data);
        toast.success(t('payments.paymentRegistered'));
      }
      setShowForm(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('payments.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    if (!hasField('view_amounts')) return '—';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredPayments = payments.filter(p => {
    const matchesSearch = !searchTerm || 
      p.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClient = !filterClient || p.client_id === filterClient;
    return matchesSearch && matchesClient;
  });

  const filteredBalances = balances.balances?.filter(b => 
    !searchTerm || b.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="payments-page">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-heading tracking-tight">{t('payments.paymentsBalance')}</h1>
            <p className="text-sm text-muted-foreground">{t('payments.paymentsBalanceSubtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === 'admin' && (
              <Button
                variant="outline"
                onClick={handleNormalize}
                disabled={normalizing}
                title="Redondear a 2 decimales todos los costos y pagos ya registrados"
                data-testid="normalize-amounts-btn"
              >
                {normalizing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Calculator size={16} className="mr-2" />}
                Normalizar decimales
              </Button>
            )}
            <Button onClick={handleNewPayment} data-testid="new-payment-btn">
              <Plus size={16} className="mr-2" />
              {t('payments.registerPayment')}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Receipt size={16} />
              <span className="text-xs uppercase tracking-wide">{t('payments.accountsReceivable')}</span>
            </div>
            <p className="text-2xl font-heading text-amber-400">{formatCurrency(balances.totals?.total_cost)}</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Wallet size={16} />
              <span className="text-xs uppercase tracking-wide">{t('payments.totalPaid')}</span>
            </div>
            <p className="text-2xl font-heading text-emerald-400">{formatCurrency(balances.totals?.total_paid)}</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign size={16} />
              <span className="text-xs uppercase tracking-wide">{t('payments.pendingBalanceLabel')}</span>
            </div>
            <p className={`text-2xl font-heading ${(balances.totals?.total_balance || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {formatCurrency(balances.totals?.total_balance)}
            </p>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users size={16} />
              <span className="text-xs uppercase tracking-wide">{t('payments.activeClients')}</span>
            </div>
            <p className="text-2xl font-heading">{balances.totals?.client_count || 0}</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="balances" data-testid="tab-balances">
                <TrendingUp size={14} className="mr-1" />
                {t('payments.balancesByClient')}
              </TabsTrigger>
              <TabsTrigger value="payments" data-testid="tab-payments">
                <CreditCard size={14} className="mr-1" />
                {t('payments.paymentHistory')}
              </TabsTrigger>
            </TabsList>
            
            <div className="flex gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('payments.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-48"
                />
              </div>
            </div>
          </div>

          {/* Balances Tab */}
          <TabsContent value="balances" className="mt-4">
            {/* Desktop table */}
            <div className="hidden md:block bg-card border border-border rounded-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                    <th className="py-3 px-4 font-medium">{t('payments.client')}</th>
                    <th className="py-3 px-4 font-medium text-right">{t('payments.shipmentsCol')}</th>
                    <th className="py-3 px-4 font-medium text-right">{t('payments.totalCost')}</th>
                    <th className="py-3 px-4 font-medium text-right">{t('payments.paymentsCol')}</th>
                    <th className="py-3 px-4 font-medium text-right">{t('payments.totalPaid')}</th>
                    <th className="py-3 px-4 font-medium text-right">{t('payments.balanceCol')}</th>
                    <th className="py-3 px-4 font-medium w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBalances.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-muted-foreground">
                        {t('payments.noBalanceData')}
                      </td>
                    </tr>
                  ) : (
                    filteredBalances.map((b) => (
                      <tr 
                        key={b.client_id} 
                        className="border-b border-border/50 hover:bg-secondary/30 cursor-pointer" 
                        data-testid={`balance-row-${b.client_id}`}
                        onClick={() => handleViewClient(b.client_id)}
                      >
                        <td className="py-3 px-4 font-medium">{b.client_name}</td>
                        <td className="py-3 px-4 text-right text-muted-foreground">{b.shipment_count}</td>
                        <td className="py-3 px-4 text-right text-amber-400">{formatCurrency(b.total_cost)}</td>
                        <td className="py-3 px-4 text-right text-muted-foreground">{b.payment_count}</td>
                        <td className="py-3 px-4 text-right text-emerald-400">{formatCurrency(b.total_paid)}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={`inline-flex items-center gap-1 ${b.balance > 0 ? 'text-red-400' : b.balance < 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                            {b.balance > 0 ? <ArrowUpRight size={14} /> : b.balance < 0 ? <ArrowDownRight size={14} /> : null}
                            {formatCurrency(Math.abs(b.balance))}
                            {b.balance > 0 && <span className="text-xs ml-1">({t('payments.owes')})</span>}
                            {b.balance < 0 && <span className="text-xs ml-1">({t('payments.inFavor')})</span>}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); handleViewClient(b.client_id); }}>
                            <Eye size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3" data-testid="mobile-balances-list">
              {filteredBalances.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground bg-card border border-border rounded-sm">
                  No hay datos de balance
                </div>
              ) : (
                filteredBalances.map((b) => (
                  <button
                    key={b.client_id}
                    type="button"
                    onClick={() => handleViewClient(b.client_id)}
                    className="w-full text-left bg-card border border-border rounded-sm p-4 hover:bg-secondary/30 active:bg-secondary/50 transition-colors"
                    data-testid={`balance-row-${b.client_id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-base truncate">{b.client_name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {b.shipment_count} embarque{b.shipment_count !== 1 ? 's' : ''} · {b.payment_count} pago{b.payment_count !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <span className={`inline-flex items-center gap-1 text-base font-semibold whitespace-nowrap ${b.balance > 0 ? 'text-red-400' : b.balance < 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                        {b.balance > 0 ? <ArrowUpRight size={16} /> : b.balance < 0 ? <ArrowDownRight size={16} /> : null}
                        {formatCurrency(Math.abs(b.balance))}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="text-muted-foreground uppercase tracking-wide text-[10px]">Costo</div>
                        <div className="text-amber-400 font-medium">{formatCurrency(b.total_cost)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-muted-foreground uppercase tracking-wide text-[10px]">Pagado</div>
                        <div className="text-emerald-400 font-medium">{formatCurrency(b.total_paid)}</div>
                      </div>
                    </div>
                    {b.balance !== 0 && (
                      <p className={`text-xs mt-2 ${b.balance > 0 ? 'text-red-400/80' : 'text-emerald-400/80'}`}>
                        {b.balance > 0 ? 'El cliente debe este monto' : 'Saldo a favor del cliente'}
                      </p>
                    )}
                  </button>
                ))
              )}
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="mt-4">
            <div className="mb-4">
              <select
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                className="bg-secondary border border-border rounded-sm px-3 py-1.5 text-sm"
              >
                <option value="">Todos los clientes</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            
            {/* Desktop table */}
            <div className="hidden md:block bg-card border border-border rounded-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                    <th className="py-3 px-4 font-medium">Fecha</th>
                    <th className="py-3 px-4 font-medium">Cliente</th>
                    <th className="py-3 px-4 font-medium">Método</th>
                    <th className="py-3 px-4 font-medium">Referencia</th>
                    <th className="py-3 px-4 font-medium text-right">Monto</th>
                    <th className="py-3 px-4 font-medium">Notas</th>
                    <th className="py-3 px-4 font-medium w-20">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-muted-foreground">
                        No hay pagos registrados
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((p) => (
                      <tr key={p.id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`payment-row-${p.id}`}>
                        <td className="py-3 px-4">{formatDate(p.payment_date)}</td>
                        <td className="py-3 px-4 font-medium">{p.client_name}</td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            p.payment_method === 'TRANSFERENCIA' ? 'bg-blue-500/20 text-blue-400' :
                            p.payment_method === 'EFECTIVO' ? 'bg-emerald-500/20 text-emerald-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {paymentMethods.find(m => m.code === p.payment_method)?.label || p.payment_method}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground font-mono text-sm">{p.reference || '—'}</td>
                        <td className="py-3 px-4 text-right text-emerald-400 font-medium">{formatCurrency(p.amount)}</td>
                        <td className="py-3 px-4 text-muted-foreground text-sm max-w-xs truncate">{p.notes || '—'}</td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => handleEditPayment(p)} data-testid={`edit-payment-${p.id}`} title="Editar pago">
                              <Edit2 size={14} />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDeletePayment(p)} className="text-red-400 hover:text-red-300" data-testid={`delete-payment-${p.id}`} title="Eliminar pago">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3" data-testid="mobile-payments-list">
              {filteredPayments.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground bg-card border border-border rounded-sm">
                  No hay pagos registrados
                </div>
              ) : (
                filteredPayments.map((p) => (
                  <div key={p.id} className="bg-card border border-border rounded-sm p-4" data-testid={`payment-row-${p.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{p.client_name}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(p.payment_date)}</p>
                      </div>
                      <span className="text-base font-semibold text-emerald-400 whitespace-nowrap">{formatCurrency(p.amount)}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        p.payment_method === 'TRANSFERENCIA' ? 'bg-blue-500/20 text-blue-400' :
                        p.payment_method === 'EFECTIVO' ? 'bg-emerald-500/20 text-emerald-400' :
                        'bg-amber-500/20 text-amber-400'
                      }`}>
                        {paymentMethods.find(m => m.code === p.payment_method)?.label || p.payment_method}
                      </span>
                      {p.reference && (
                        <span className="text-xs text-muted-foreground font-mono">Ref: {p.reference}</span>
                      )}
                    </div>
                    {p.notes && (
                      <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{p.notes}</p>
                    )}
                    <div className="flex justify-end gap-1 pt-2 border-t border-border/50">
                      <Button size="sm" variant="outline" onClick={() => handleEditPayment(p)} className="h-9" data-testid={`edit-payment-${p.id}`}>
                        <Edit2 size={14} className="mr-1.5" />
                        Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeletePayment(p)} className="h-9 text-red-400 hover:text-red-300" data-testid={`delete-payment-${p.id}`}>
                        <Trash2 size={14} className="mr-1.5" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Payment Form Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar Pago' : 'Registrar Pago'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <select
                  value={form.client_id}
                  onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  className="w-full bg-secondary border border-border rounded-sm px-3 py-2"
                  required
                  data-testid="payment-client-select"
                >
                  <option value="">Seleccionar cliente</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monto *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="payment-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={form.payment_date}
                    onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                    required
                    data-testid="payment-date-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Método de Pago *</Label>
                <select
                  value={form.payment_method}
                  onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                  className="w-full bg-secondary border border-border rounded-sm px-3 py-2"
                  required
                  data-testid="payment-method-select"
                >
                  {paymentMethods.map((m) => (
                    <option key={m.code} value={m.code}>{m.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-2">
                <Label>Referencia / Comprobante</Label>
                <Input
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="Número de transferencia, etc."
                  data-testid="payment-reference-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Observaciones adicionales..."
                  rows={2}
                  data-testid="payment-notes-input"
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} data-testid="payment-submit-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editing ? 'Actualizar' : 'Registrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Client Detail Dialog */}
        <Dialog open={!!selectedClient} onOpenChange={(open) => !open && handleCloseDetail()}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users size={20} />
                Detalle de Cliente: {clientDetail?.client_name}
              </DialogTitle>
            </DialogHeader>
            
            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : clientDetail ? (
              <div className="space-y-6">
                {/* Initial Balance Form */}
                {showInitialBalanceForm ? (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4">
                    <h4 className="font-medium text-amber-400 mb-3">Establecer Saldo Inicial</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Monto *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={initialBalanceForm.amount}
                          onChange={(e) => setInitialBalanceForm({...initialBalanceForm, amount: e.target.value})}
                          placeholder="823694.25"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Fecha (al) *</Label>
                        <Input
                          type="date"
                          value={initialBalanceForm.as_of_date}
                          onChange={(e) => setInitialBalanceForm({...initialBalanceForm, as_of_date: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <Label className="text-xs">Notas</Label>
                      <Input
                        value={initialBalanceForm.notes}
                        onChange={(e) => setInitialBalanceForm({...initialBalanceForm, notes: e.target.value})}
                        placeholder="Saldo pendiente de operaciones anteriores"
                      />
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => setShowInitialBalanceForm(false)}>
                        Cancelar
                      </Button>
                      <Button size="sm" onClick={handleSaveInitialBalance} disabled={savingInitialBalance}>
                        {savingInitialBalance ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Guardar
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Initial Balance Banner (if exists) or Add button */
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3 flex justify-between items-start">
                    {clientDetail.initial_balance > 0 ? (
                      <div>
                        <p className="text-sm text-amber-400">
                          <strong>Saldo Inicial:</strong> {formatCurrency(clientDetail.initial_balance)} 
                          {clientDetail.initial_balance_date && (
                            <span className="text-muted-foreground ml-2">(al {formatDate(clientDetail.initial_balance_date)})</span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Deuda de operaciones anteriores a OFIMEX</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-muted-foreground">Sin saldo inicial registrado</p>
                        <p className="text-xs text-muted-foreground">Agrega el saldo que tenía este cliente antes de usar OFIMEX</p>
                      </div>
                    )}
                    <Button size="sm" variant="outline" onClick={handleEditInitialBalance}>
                      <Edit2 size={12} className="mr-1" />
                      {clientDetail.initial_balance > 0 ? 'Editar' : 'Agregar'}
                    </Button>
                  </div>
                )}

                {/* Summary Cards */}
                <div className={`grid gap-3 ${clientDetail.initial_balance > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                  {clientDetail.initial_balance > 0 && (
                    <div className="bg-secondary/30 rounded-sm p-3">
                      <p className="text-xs text-muted-foreground uppercase mb-1">{t('payments.initialBalance')}</p>
                      <p className="text-lg font-heading text-amber-400">{formatCurrency(clientDetail.initial_balance)}</p>
                    </div>
                  )}
                  <div className="bg-secondary/30 rounded-sm p-3">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Costo Embarques</p>
                    <p className="text-lg font-heading text-amber-400">{formatCurrency(clientDetail.shipments_cost || 0)}</p>
                    <p className="text-xs text-muted-foreground">{clientDetail.shipment_count} embarques</p>
                  </div>
                  <div className="bg-secondary/30 rounded-sm p-3">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Total Pagado</p>
                    <p className="text-lg font-heading text-emerald-400">{formatCurrency(clientDetail.total_paid)}</p>
                    <p className="text-xs text-muted-foreground">{clientDetail.payment_count} pagos</p>
                  </div>
                  <div className="bg-secondary/30 rounded-sm p-3">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Saldo Pendiente</p>
                    <p className={`text-lg font-heading ${clientDetail.balance > 0 ? 'text-red-400' : clientDetail.balance < 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                      {formatCurrency(Math.abs(clientDetail.balance))}
                      {clientDetail.balance > 0 && <span className="text-xs ml-1">(Debe)</span>}
                      {clientDetail.balance < 0 && <span className="text-xs ml-1">(A favor)</span>}
                    </p>
                  </div>
                </div>

                {/* LIN: Desglose por Destino Final (Saltillo / Tezoyuca) */}
                {(clientDetail.client_name || '').toUpperCase().includes('LIN') && clientDetail.shipments?.length > 0 && (() => {
                  const buckets = { SALTILLO: { count: 0, cost: 0 }, TEZOYUCA: { count: 0, cost: 0 }, OTROS: { count: 0, cost: 0 } };
                  clientDetail.shipments.forEach((s) => {
                    const dest = (s.destination || '').toString().toLowerCase();
                    const cost = parseFloat(s.total_cost || 0);
                    let key = 'OTROS';
                    if (dest.includes('saltillo')) key = 'SALTILLO';
                    else if (dest.includes('tezoyuca')) key = 'TEZOYUCA';
                    buckets[key].count += 1;
                    buckets[key].cost += cost;
                  });
                  return (
                    <div data-testid="lin-zone-breakdown">
                      <h3 className="font-medium mb-2 flex items-center gap-2">
                        <Ship size={16} className="text-purple-400" />
                        Desglose por Destino Final
                        <span className="text-xs text-muted-foreground font-normal">(separación de cobranza por zona)</span>
                      </h3>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-3" data-testid="zone-saltillo">
                          <p className="text-xs uppercase tracking-wide text-blue-400 mb-1">Saltillo</p>
                          <p className="text-lg font-heading text-amber-400">{formatCurrency(buckets.SALTILLO.cost)}</p>
                          <p className="text-xs text-muted-foreground">{buckets.SALTILLO.count} embarques</p>
                        </div>
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-sm p-3" data-testid="zone-tezoyuca">
                          <p className="text-xs uppercase tracking-wide text-emerald-400 mb-1">Tezoyuca</p>
                          <p className="text-lg font-heading text-amber-400">{formatCurrency(buckets.TEZOYUCA.cost)}</p>
                          <p className="text-xs text-muted-foreground">{buckets.TEZOYUCA.count} embarques</p>
                        </div>
                        <div className="bg-zinc-500/10 border border-zinc-500/30 rounded-sm p-3" data-testid="zone-otros">
                          <p className="text-xs uppercase tracking-wide text-zinc-400 mb-1">Otros / Sin destino</p>
                          <p className="text-lg font-heading text-amber-400">{formatCurrency(buckets.OTROS.cost)}</p>
                          <p className="text-xs text-muted-foreground">{buckets.OTROS.count} embarques</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Shipments Table */}
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Ship size={16} className="text-blue-400" />
                    Embarques ({clientDetail.shipments?.length || 0})
                  </h3>
                  <div className="bg-card border border-border rounded-sm overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-secondary/50">
                        <tr className="text-left text-xs text-muted-foreground border-b border-border">
                          <th className="py-2 px-3">MBL</th>
                          <th className="py-2 px-3">Estatus</th>
                          <th className="py-2 px-3 text-right">Costo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientDetail.shipments?.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="py-4 text-center text-muted-foreground">Sin embarques</td>
                          </tr>
                        ) : (
                          clientDetail.shipments?.map((s) => (
                            <tr key={s.mbl} className="border-b border-border/30 hover:bg-secondary/30">
                              <td className="py-2 px-3">
                                <Link 
                                  to={`/shipments/${s.mbl}`} 
                                  className="text-primary hover:underline font-mono"
                                  onClick={handleCloseDetail}
                                >
                                  {s.mbl}
                                </Link>
                              </td>
                              <td className="py-2 px-3">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  s.status_general_code === 'ENTREGADO' ? 'bg-emerald-500/20 text-emerald-400' :
                                  s.status_general_code === 'EN_PUERTO' ? 'bg-amber-500/20 text-amber-400' :
                                  'bg-zinc-500/20 text-zinc-400'
                                }`}>
                                  {s.status_general_code || 'PENDIENTE'}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-right text-amber-400">{formatCurrency(s.total_cost)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payments Table */}
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <CreditCard size={16} className="text-emerald-400" />
                    Pagos Recibidos ({clientDetail.payments?.length || 0})
                  </h3>
                  <div className="bg-card border border-border rounded-sm overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-secondary/50">
                        <tr className="text-left text-xs text-muted-foreground border-b border-border">
                          <th className="py-2 px-3">Fecha</th>
                          <th className="py-2 px-3">Método</th>
                          <th className="py-2 px-3">Referencia</th>
                          <th className="py-2 px-3 text-right">Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientDetail.payments?.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="py-4 text-center text-muted-foreground">Sin pagos registrados</td>
                          </tr>
                        ) : (
                          clientDetail.payments?.map((p) => (
                            <tr key={p.id} className="border-b border-border/30 hover:bg-secondary/30">
                              <td className="py-2 px-3">{formatDate(p.payment_date)}</td>
                              <td className="py-2 px-3">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  p.payment_method === 'TRANSFERENCIA' ? 'bg-blue-500/20 text-blue-400' :
                                  p.payment_method === 'EFECTIVO' ? 'bg-emerald-500/20 text-emerald-400' :
                                  'bg-amber-500/20 text-amber-400'
                                }`}>
                                  {paymentMethods.find(m => m.code === p.payment_method)?.label || p.payment_method}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-muted-foreground font-mono text-xs">{p.reference || '—'}</td>
                              <td className="py-2 px-3 text-right text-emerald-400">{formatCurrency(p.amount)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payments by Method Summary */}
                {clientDetail.payments_by_method && Object.keys(clientDetail.payments_by_method).length > 0 && (
                  <div>
                    <h3 className="font-medium mb-2">{t('payments.paymentMethodSummary')}</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(clientDetail.payments_by_method).map(([method, data]) => (
                        <div key={method} className="bg-secondary/30 rounded-sm p-2 text-sm">
                          <p className="text-muted-foreground">{paymentMethods.find(m => m.code === method)?.label || method}</p>
                          <p className="text-emerald-400 font-medium">{formatCurrency(data.total)}</p>
                          <p className="text-xs text-muted-foreground">{data.count} pagos</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                  <Button variant="outline" onClick={handleCloseDetail}>
                    Cerrar
                  </Button>
                  <Button onClick={() => { 
                    setForm({ ...form, client_id: clientDetail.client_id }); 
                    handleCloseDetail(); 
                    setShowForm(true); 
                  }}>
                    <Plus size={14} className="mr-2" />
                    Registrar Pago
                  </Button>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
